import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { parseSse } from "../../api/assistant";
import { chatApi, readStreamEvent } from "../../api/chat";
import type { ChatChannel, ChatPerson, ChatStreamEvent } from "../../api/chat";
import { refreshAfterUnauthorized } from "../../api/client";

export type ChatStatus = "off" | "connecting" | "live" | "reconnecting";

/**
 * The chat as the whole app sees it: the user's channels with their unread counts, the people, who is on, and one
 * stream of changes. It lives in the app shell, so the menu's count stays current on every screen; the chat screen
 * reads it through {@link useChat}.
 */
export type ChatConnection = {
  enabled: boolean;
  me: string;
  status: ChatStatus;
  /** Null until the first list arrives. */
  channels: ChatChannel[] | null;
  people: Map<string, ChatPerson>;
  online: Set<string>;
  /** Unread messages across the channels the user is in. */
  unread: number;
  subscribe: (listener: (e: ChatStreamEvent) => void) => () => void;
  refreshChannels: () => Promise<void>;
  refreshPeople: () => Promise<void>;
  /** The channel on screen. Its new messages are read as they come, so they do not add to the count. */
  setOpenChannel: (id: string | null) => void;
  /** A channel the API has just returned (made, joined, renamed), placed in the list without waiting for it. */
  upsertChannel: (channel: ChatChannel) => void;
  removeChannel: (id: string) => void;
};

export const ChatContext = createContext<ChatConnection | null>(null);

/** Outside the app shell — a screen drawn on its own, as the screen gate in the tests does — the chat is off. */
const OFF: ChatConnection = {
  enabled: false, me: "", status: "off", channels: null, people: new Map(), online: new Set(), unread: 0,
  subscribe: () => () => {},
  refreshChannels: async () => {},
  refreshPeople: async () => {},
  setOpenChannel: () => {},
  upsertChannel: () => {},
  removeChannel: () => {},
};

export function useChat(): ChatConnection {
  return useContext(ChatContext) ?? OFF;
}

const STREAM_URL = "/api/chat/stream";
const RETRY_MAX_MS = 30_000;

function sortChannels(list: ChatChannel[]): ChatChannel[] {
  return [...list].sort((a, b) =>
    a.defaultChannel === b.defaultChannel ? a.name.localeCompare(b.name) : a.defaultChannel ? -1 : 1,
  );
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, ms);
    signal.addEventListener("abort", () => { clearTimeout(timer); resolve(); }, { once: true });
  });
}

export function useChatConnection(enabled: boolean, me: string): ChatConnection {
  const [status, setStatus] = useState<ChatStatus>(enabled ? "connecting" : "off");
  const [channels, setChannels] = useState<ChatChannel[] | null>(null);
  const [people, setPeople] = useState<Map<string, ChatPerson>>(() => new Map());
  const [online, setOnline] = useState<Set<string>>(() => new Set());
  const listeners = useRef(new Set<(e: ChatStreamEvent) => void>());
  const openChannel = useRef<string | null>(null);
  // The list as last rendered, for the stream handler, which outlives any one render.
  const channelsRef = useRef(channels);
  channelsRef.current = channels;

  const refreshChannels = useCallback(async () => {
    try {
      setChannels(sortChannels(await chatApi.channels()));
    } catch {
      // The list stays as it was; the next event or reconnect tries again.
    }
  }, []);

  const refreshPeople = useCallback(async () => {
    try {
      const list = await chatApi.people();
      setPeople(new Map(list.map((p) => [p.id, p])));
    } catch {
      // Names fall back to what is already known.
    }
  }, []);

  useEffect(() => {
    if (!enabled || !me) {
      setStatus("off");
      return;
    }
    const controller = new AbortController();
    const { signal } = controller;

    function handle(ev: ChatStreamEvent) {
      switch (ev.type) {
        case "ready":
          setOnline(new Set(ev.online));
          // Whatever happened while the stream was down is in the list now.
          void refreshChannels();
          break;
        case "online":
          setOnline(new Set(ev.users));
          break;
        case "message": {
          const m = ev.message;
          // A reply counts only when it is for the user: named in it, or in a thread they follow.
          const forMe = m.mentions.includes(me) || (m.parentId !== null && m.followers.includes(me));
          if (m.parentId !== null && !forMe) break;
          const seen = m.authorId === me
            || (openChannel.current === m.channelId && document.visibilityState === "visible");
          // A channel the list does not have yet (joined in another tab): read the list again.
          if (!channelsRef.current?.some((c) => c.id === m.channelId)) {
            void refreshChannels();
            break;
          }
          setChannels((list) => list?.map((c) => {
            if (c.id !== m.channelId) return c;
            return seen
              ? { ...c, lastMessageAt: m.createdAt }
              : { ...c, lastMessageAt: m.createdAt, unread: c.unread + 1,
                  mentions: c.mentions + (forMe ? 1 : 0) };
          }) ?? list);
          break;
        }
        case "read":
          setChannels((list) => list?.map((c) => (c.id === ev.channelId
            ? { ...c, unread: 0, mentions: 0, lastReadAt: ev.lastReadAt ?? c.lastReadAt }
            : c)) ?? list);
          break;
        case "channels":
        case "followed":
          // A thread followed or left changes what counts as unread.
          void refreshChannels();
          break;
        default:
          break;
      }
      listeners.current.forEach((listener) => listener(ev));
    }

    async function run() {
      let wait = 1000;
      let refusals = 0;
      while (!signal.aborted) {
        try {
          const res = await fetch(STREAM_URL, {
            credentials: "include",
            cache: "no-store",
            headers: { Accept: "text/event-stream" },
            signal,
          });
          if (res.status === 401) {
            // The access token rotates every fifteen minutes; refresh it as any other request would.
            if (++refusals > 2 || !(await refreshAfterUnauthorized())) return;
            continue;
          }
          if (res.status === 403) {
            setStatus("off");
            return;
          }
          if (!res.ok || !res.body) throw new Error(`stream ${res.status}`);
          refusals = 0;
          wait = 1000;
          setStatus("live");
          const reader = res.body.getReader();
          const decoder = new TextDecoder();
          let buffer = "";
          for (;;) {
            const { value, done } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const { events, rest } = parseSse(buffer);
            buffer = rest;
            for (const frame of events) {
              const ev = readStreamEvent(frame.event, frame.data);
              if (ev) handle(ev);
            }
          }
          // The server ends each stream after a few minutes; the next one opens at once and says "ready".
        } catch {
          if (signal.aborted) return;
          setStatus("reconnecting");
          await sleep(wait, signal);
          wait = Math.min(wait * 2, RETRY_MAX_MS);
        }
      }
    }

    setStatus("connecting");
    void refreshChannels();
    void refreshPeople();
    void run();
    return () => controller.abort();
  }, [enabled, me, refreshChannels, refreshPeople]);

  const subscribe = useCallback((listener: (e: ChatStreamEvent) => void) => {
    listeners.current.add(listener);
    return () => { listeners.current.delete(listener); };
  }, []);

  const setOpenChannel = useCallback((id: string | null) => {
    openChannel.current = id;
  }, []);

  const upsertChannel = useCallback((channel: ChatChannel) => {
    setChannels((list) => sortChannels([...(list ?? []).filter((c) => c.id !== channel.id), channel]));
  }, []);

  const removeChannel = useCallback((id: string) => {
    setChannels((list) => list?.filter((c) => c.id !== id) ?? list);
  }, []);

  const unread = useMemo(
    () => (channels ?? []).filter((c) => c.member).reduce((sum, c) => sum + c.unread, 0),
    [channels],
  );

  return useMemo(
    () => ({ enabled, me, status, channels, people, online, unread, subscribe, refreshChannels, refreshPeople,
             setOpenChannel, upsertChannel, removeChannel }),
    [enabled, me, status, channels, people, online, unread, subscribe, refreshChannels, refreshPeople,
     setOpenChannel, upsertChannel, removeChannel],
  );
}
